import { Preferences } from '@capacitor/preferences'

import type { CognitoTokens } from '@/api/auth/types/LoginResponse'

import { isNativePlatform } from './platform'

export const AUTH_TOKENS_KEY = 'cognito-tokens'

let memoryTokens: CognitoTokens | null = null
let hydrated = false

const isTokenShape = (value: unknown): value is CognitoTokens => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.accessToken === 'string' && typeof record.refreshToken === 'string'
}

const parseTokens = (raw: string | null): CognitoTokens | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return isTokenShape(parsed) ? parsed : null
  } catch {
    return null
  }
}

const readLocalStorage = (): CognitoTokens | null => {
  if (typeof localStorage === 'undefined') return null
  return parseTokens(localStorage.getItem(AUTH_TOKENS_KEY))
}

const writeLocalStorage = (tokens: CognitoTokens | null) => {
  if (typeof localStorage === 'undefined') return
  if (tokens) localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokens))
  else localStorage.removeItem(AUTH_TOKENS_KEY)
}

export async function hydrateAuthTokens(): Promise<CognitoTokens | null> {
  if (hydrated && memoryTokens) return memoryTokens

  if (isNativePlatform()) {
    try {
      const stored = await Preferences.get({ key: AUTH_TOKENS_KEY })
      const fromPrefs = parseTokens(stored.value)
      if (fromPrefs) {
        memoryTokens = fromPrefs
        writeLocalStorage(fromPrefs)
        hydrated = true
        return fromPrefs
      }
    } catch {
      // Fall through to localStorage
    }
  }

  memoryTokens = readLocalStorage()
  hydrated = true
  return memoryTokens
}

export function getAuthTokens(): CognitoTokens | null {
  if (memoryTokens) return memoryTokens
  memoryTokens = readLocalStorage()
  return memoryTokens
}

export async function setAuthTokens(tokens: CognitoTokens): Promise<void> {
  memoryTokens = tokens
  hydrated = true
  writeLocalStorage(tokens)
  if (!isNativePlatform()) return
  await Preferences.set({ key: AUTH_TOKENS_KEY, value: JSON.stringify(tokens) })
}

export async function clearAuthTokens(): Promise<void> {
  memoryTokens = null
  hydrated = true
  writeLocalStorage(null)
  if (!isNativePlatform()) return
  await Preferences.remove({ key: AUTH_TOKENS_KEY })
}

export function setAuthTokensSync(tokens: CognitoTokens): void {
  memoryTokens = tokens
  hydrated = true
  writeLocalStorage(tokens)
  if (isNativePlatform()) {
    void Preferences.set({ key: AUTH_TOKENS_KEY, value: JSON.stringify(tokens) })
  }
}

export function clearAuthTokensSync(): void {
  memoryTokens = null
  hydrated = true
  writeLocalStorage(null)
  if (isNativePlatform()) {
    void Preferences.remove({ key: AUTH_TOKENS_KEY })
  }
}
